// Referral + UTM attribution capture/persistence.
// A visitor may land on any page via a referral or campaign link and navigate
// around before reaching signup/consultation/checklist — so we capture on every
// page load and persist in sessionStorage (not localStorage: attribution should
// not live forever) until it's consumed at signup or lead submission.

const REF_KEY = "cs_referral_code";
const UTM_SOURCE_KEY = "cs_utm_source";
const UTM_MEDIUM_KEY = "cs_utm_medium";
const UTM_CAMPAIGN_KEY = "cs_utm_campaign";

function safeGet(key) {
  try {
    return sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function safeSet(key, value) {
  try {
    if (value) sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function safeClear(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

// Call once near the top of the app (e.g. in ScrollToTop / a route-level effect)
// to capture ?ref=, ?referral_code=, and utm_* params from the current URL.
export function captureAttributionFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref") || params.get("referral_code");
  if (ref) safeSet(REF_KEY, ref.trim().toUpperCase());
  const utmSource = params.get("utm_source");
  const utmMedium = params.get("utm_medium");
  const utmCampaign = params.get("utm_campaign");
  if (utmSource) safeSet(UTM_SOURCE_KEY, utmSource);
  if (utmMedium) safeSet(UTM_MEDIUM_KEY, utmMedium);
  if (utmCampaign) safeSet(UTM_CAMPAIGN_KEY, utmCampaign);
}

// Returns the referral code detected via URL or sessionStorage (uppercase), or "".
export function getDetectedReferralCode() {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("ref") || params.get("referral_code");
  if (fromUrl) return fromUrl.trim().toUpperCase();
  return safeGet(REF_KEY).toUpperCase();
}

// Full attribution bundle for lead/signup submissions.
export function getLeadAttribution() {
  const referral_code = getDetectedReferralCode() || null;
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const utm_source = params.get("utm_source") || safeGet(UTM_SOURCE_KEY) || null;
  const utm_medium = params.get("utm_medium") || safeGet(UTM_MEDIUM_KEY) || null;
  const utm_campaign = params.get("utm_campaign") || safeGet(UTM_CAMPAIGN_KEY) || null;
  const source = referral_code ? "referral" : (utm_source || "website");
  return { source, referral_code, utm_source, utm_medium, utm_campaign };
}

// Call after a successful signup/registration to clear the temporary referral
// attribution — it has now been permanently attached to the new account.
export function clearReferralAttribution() {
  safeClear(REF_KEY);
}

export function buildReferralLink(referralCode) {
  if (typeof window === "undefined" || !referralCode) return "";
  return `${window.location.origin}/signup?ref=${referralCode}`;
}
